import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { API_TYPES, ITEM_ICONS, STRING_UOM } from '@/main/app.constants';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import { setEvaluationResults, setTableDefinition } from '@/tableDefinitionEditor/tableDefinition.actions';
import {
  consolidatedValueFormatter,
  getCellEvaluationResults,
  isItemTableCell,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { requestTableReloadAfterOverride } from '@/tableDefinitionEditor/tableDefinitionOverrideReload.utilities';
import { setNameInAllTools } from '@/toolSelection/investigate.actions';
import { setTrendItemProps } from '@/trendData/trend.actions';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { Icon } from '@seeqdev/qomponents';
import type { CustomCellRendererProps } from 'ag-grid-react';
import type {
  ColumnDefinition,
  EditorType,
  EvaluationDetailsCellContext,
  ItemTableCell,
  ProcessedTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { EvaluationDetailsModalData } from '@/tableDefinitionEditor/tableDefinitionEditor.store';
import type { RulesResultV1 } from '@seeqdev/api/model/RulesResultV1';
import type { IconType } from '@seeqdev/qomponents';

export interface TableDefinitionRendererProps extends CustomCellRendererProps {
  tableDefinitionId: string;
  tableDefinitionColumnDef: ColumnDefinition;
  /** Present for table-definition editors; omitted when Stored Table reuses this renderer. */
  editorType?: EditorType;
  onOpenEvaluationDetails?: (data: EvaluationDetailsModalData) => void;
}

const deleteOverride = async (props: TableDefinitionRendererProps) => {
  if (props.colDef?.field === undefined || props.api === undefined || props.editorType === undefined) {
    return;
  }
  const columns = props.api.getColumns();
  const columnIdItemId = columns
    ?.find((c) => c.getColDef().headerName === SeeqNames.MaterializedTables.ItemIdColumn)
    ?.getColId();

  const columnIdDatumId = columns
    ?.find((c) => c.getColDef().headerName === SeeqNames.MaterializedTables.DatumIdColumn)
    ?.getColId();

  const itemId: string = columnIdItemId ? props.data[columnIdItemId].uuid : props.tableDefinitionId;
  const datumId: string = columnIdDatumId ? props.data[columnIdDatumId].value : undefined;

  try {
    const { data: computeData } = await sqTableDefinitionsApi.deleteOverridesFromColumn({
      id: props.tableDefinitionId,
      columnId: props.colDef.field,
      itemId,
      datumId,
    });
    setTableDefinition(props.editorType, computeData.tableDefinition);
    setEvaluationResults(props.editorType, computeData.computeResults);
    requestTableReloadAfterOverride(props.editorType, props.colDef.field);
    successToast({ messageKey: 'SCALING.OVERRIDE.OVERRIDE_DELETE_SUCCESS' });
    props.api.clearFocusedCell();
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

const deleteManagedOutsideTableProp = async (props: TableDefinitionRendererProps) => {
  if (props.colDef?.field === undefined || props.editorType === undefined) {
    return;
  }
  try {
    await sqItemsApi.deleteProperty({
      id: props.value.uuid,
      propertyName: SeeqNames.Properties.IsModifiedOutsideTable,
    });
    const { data: computeData } = await sqTableDefinitionsApi.recomputeColumnInTableDefinition(
      { rowIds: [] },
      {
        id: props.tableDefinitionId,
        columnId: props.colDef.field,
        includeDetailedResults: true,
      },
    );
    setTableDefinition(props.editorType, computeData.tableDefinition);
    setEvaluationResults(props.editorType, computeData.computeResults);

    const { data: propertyData } = await sqItemsApi.getProperty({
      id: props.value.uuid,
      propertyName: SeeqNames.Properties.Name,
    });
    if (propertyData?.value) {
      setNameInAllTools(propertyData.value, props.value.uuid);
      setTrendItemProps(props.value.uuid, { name: propertyData.value }, PUSH_IGNORE);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Subscribes to the cell's rule evaluation results so the details icon appears/clears reactively when the cache
 * changes (e.g. after an override). An imperative `refreshCells` is unreliable under ag-grid's
 * reactiveCustomComponents — after coalescing the reload the overridden cell's value is unchanged, so nothing else
 * re-runs the renderer and the icon never shows.
 */
const TableDefinitionCellRendererWithEvalSubscription = (
  props: TableDefinitionRendererProps & { editorType: EditorType },
) => {
  const rowNodeId = props.node?.id;
  const colKey = props.colDef?.field;
  const evaluationDetails = useFluxPath(getTableDefinitionStore(props.editorType), (store) =>
    rowNodeId && colKey ? getCellEvaluationResults(store.cellEvaluationResultsCache, rowNodeId, colKey) : undefined,
  );
  return <TableDefinitionCellRendererView {...props} evaluationDetails={evaluationDetails} />;
};

export const TableDefinitionCellRenderer = (props: TableDefinitionRendererProps) => {
  // Stored Table reuses this renderer without `editorType` and has no eval-results cache — skip the store lookup
  // that would throw `Unknown editor type: undefined`.
  if (!props.editorType) {
    return <TableDefinitionCellRendererView {...props} evaluationDetails={undefined} />;
  }
  return <TableDefinitionCellRendererWithEvalSubscription {...props} editorType={props.editorType} />;
};

const TableDefinitionCellRendererView = (
  props: TableDefinitionRendererProps & { evaluationDetails: RulesResultV1[] | undefined },
) => {
  const rowNodeId = props.node?.id;
  const colKey = props.colDef?.field;
  const { evaluationDetails } = props;

  const isGroupRow = props.colDef?.showRowGroup;
  if (isGroupRow) {
    return <>{props.node?.key}</>;
  }

  let valueFormatted = props.valueFormatted;

  let cellData = props.colDef?.field && props.data && props.data[props.colDef.field];
  if (!cellData) {
    cellData = props.value;
    valueFormatted = consolidatedValueFormatter({ value: cellData, colDef: props.colDef });
  }
  if (!cellData) {
    return undefined;
  }

  // AG Grid's group-safe value formatter returns undefined for a group row whose column defines an aggFunc (see
  // createGroupSafeValueFormatter). SSRM group rows carry their row-lifted data on the node, so the grid supplies no
  // formatted value even though the cell has one; recompute it from the lifted cell here so the group row renders it.
  if (valueFormatted == null) {
    valueFormatted = consolidatedValueFormatter({ value: cellData, colDef: props.colDef });
  }

  let remove = <></>;
  let info = <></>;
  let modifiedOutsideTable = <></>;

  if (cellData.override) {
    remove = (
      <Icon
        testId="removeIconOverride"
        icon="fa-times"
        large={true}
        extraClassNames="closeIcon ml5"
        onClick={() => deleteOverride(props)}
        tooltip={t('SCALING.OVERRIDE.DELETE_OVERRIDE_TOOLTIP')}
      />
    );
  }

  if (cellData.isModifiedOutsideTable) {
    modifiedOutsideTable = (
      <Icon
        testId="modifiedOutsideTableIcon"
        icon="fa-right-from-bracket"
        large={true}
        extraClassNames="mr5"
        tooltip={t('SCALING.ITEM_MANAGED_OUTSIDE_TABLE')}
      />
    );
    remove = (
      <Icon
        testId="removeIconOutsideTable"
        icon="fa-times"
        large={true}
        extraClassNames="closeIcon ml5"
        onClick={() => deleteManagedOutsideTableProp(props)}
        tooltip={t('SCALING.REMOVE_ITEM_MANAGED_OUTSIDE_TABLE')}
      />
    );
  }

  if (evaluationDetails) {
    const isEmpty = !cellData.value && !cellData.uuid;
    let iconString = 'fa-info-circle';
    let iconType: IconType = 'theme';
    if (isEmpty) {
      iconString = 'fa-warning';
      iconType = 'warning';
    }
    const cellContext: EvaluationDetailsCellContext | undefined =
      rowNodeId !== undefined && colKey !== undefined ? { rowNodeId, colKey, isEmpty } : undefined;
    info = (
      <Icon
        testId="evaluationDetailsIcon"
        icon={iconString}
        large={true}
        type={iconType}
        extraClassNames="mr5"
        onClick={() => {
          if (props.tableDefinitionColumnDef && props.onOpenEvaluationDetails) {
            props.onOpenEvaluationDetails({
              evaluationColumn: props.tableDefinitionColumnDef,
              evaluationResults: evaluationDetails,
              cellContext,
            });
          }
        }}
        tooltip={t('SCALING.EVALUATION_DETAILS_TOOLTIP')}
      />
    );
  }

  const icon = (itemCellData: ItemTableCell) => {
    const properties = itemCellData.fetchedProperties;
    if (!properties) {
      return <></>;
    }

    const valueUom = properties[SeeqNames.Properties.ValueUom];
    const seriesIcon = valueUom === STRING_UOM ? ITEM_ICONS.STRING_SERIES : ITEM_ICONS.SERIES;

    const itemIconMapping = {
      [API_TYPES.ASSET]: ITEM_ICONS.ASSET,
      [API_TYPES.CALCULATED_CONDITION]: ITEM_ICONS.CONDITION,
      [API_TYPES.CALCULATED_SCALAR]: ITEM_ICONS.SCALAR,
      [API_TYPES.CALCULATED_SIGNAL]: seriesIcon,
      [API_TYPES.CAPSULE]: ITEM_ICONS.CAPSULE,
      [API_TYPES.LITERAL_SCALAR]: ITEM_ICONS.SCALAR,
      [API_TYPES.STORED_SIGNAL]: seriesIcon,
    };

    const itemType = properties[SeeqNames.Properties.Type];
    const iconClass = (itemType && itemIconMapping[itemType]) || ITEM_ICONS.ASSET;

    return <Icon icon={iconClass} extraClassNames="mr5" />;
  };

  return (
    <>
      {icon(cellData)}
      {info}
      {modifiedOutsideTable}
      {valueFormatted}
      {remove}
    </>
  );
};

export const AssetBuilderGroupHeader = (params: CustomCellRendererProps) => {
  let value: string | undefined;
  const cell = params.getValue?.() as ProcessedTableCell | undefined;
  if (isItemTableCell(cell)) {
    value = cell.fetchedProperties?.[SeeqNames.Properties.Name];
  } else if (cell) {
    value = consolidatedValueFormatter({ value: cell });
  }
  return <span>{value}</span>;
};
